import {useAuth} from 'app/modules/auth'
import {FC, useEffect, useState} from 'react'
import db from 'services/firebase'
import {addDoc, collection, deleteDoc, doc, serverTimestamp, Timestamp} from '@firebase/firestore'
import {showSuccessToast} from 'app/components/ShowToast'
import AddEditFAQModal from './modals/AddEditFAQModal'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Card from './Card'
import EditButton from './EditButton'
import {toAbsoluteUrl} from '_metronic/helpers'
import Button from 'app/components/Button'
import {Accordion} from 'react-bootstrap'
import CustomDrawer from './Drawer'

export type FAQDataType = {
  id: string
  question: string
  answer: string
  addedBy?: string
  updatedAt?: Timestamp
}

type Props = {
  addedFAQs: FAQDataType[]
}

const FAQ: FC<Props> = ({addedFAQs}) => {
  const {currentUser} = useAuth()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [toEditFAQ, setToEditFAQ] = useState<FAQDataType>({
    id: '',
    question: '',
    answer: '',
  })

  const [toDeleteFAQ, setToDeleteFAQ] = useState<FAQDataType>()

  const [loading, setLoading] = useState(false)

  const [activeKey, setActiveKey] = useState('')
  const [activeKeyForDrawer, setActiveKeyForDrawer] = useState('')

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  const handleRemoveFAQ = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const faqsRef = collection(db, 'tenants', tenantId, 'trust_center', 'faqs', 'all_faqs')

      await deleteDoc(doc(faqsRef, toDeleteFAQ?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeleteFAQ?.question} FAQ for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'FAQ removed',
      })

      showSuccessToast({message: 'FAQ has been removed successfully'})
    } catch (error) {
      console.error('Error while removing FAQ: ', error)
    } finally {
      setLoading(false)
      setToDeleteFAQ(undefined)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <div>
      <CustomDrawer isDrawerOpen={isDrawerOpen} onCancel={onCancel} title='Frequently Asked Questions'>
        <div style={{padding: '20px 20px 80px'}}>
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Add all common questions and answers you want to share with your customers.
          </p>

          <div className='d-flex flex-column' style={{gap: '16px'}}>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1'>
              <span className='text-dark fs-6 fw-bold'>Frequently Asked Questions</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setToEditFAQ({
                    id: '',
                    question: '',
                    answer: '',
                    addedBy: '',
                  })
                  setIsModalOpen(true)
                }}
              >
                <img
                  alt='plus-icon'
                  src={toAbsoluteUrl('/media/icon/plus.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Add FAQ</span>
              </Button>
            </div>

            {addedFAQs.length > 0 ? (
              <Accordion className='neutral-accordion' activeKey={activeKeyForDrawer}>
                {addedFAQs.map((faq, index) => (
                  <div
                    key={faq.id}
                    className='d-flex align-items-center'
                    style={{
                      gap: '12px',
                      marginBottom: '12px',
                      padding: '0.5px',
                    }}
                  >
                    <div
                      className='flex-fill'
                      style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        padding: '0.5px',
                      }}
                    >
                      <Accordion.Item
                        eventKey={`${index}`}
                        style={{
                          border: 'none',
                        }}
                      >
                        <Accordion.Header
                          onClick={() =>
                            setActiveKeyForDrawer(
                              activeKeyForDrawer === `${index}` ? '' : `${index}`
                            )
                          }
                        >
                          <span className='fw-medium fs-6 text-dark'>{faq.question}</span>
                        </Accordion.Header>

                        <Accordion.Body className='text-secondary-gray' style={{fontSize: '13px'}}>
                          {faq.answer}
                        </Accordion.Body>
                      </Accordion.Item>
                    </div>

                    <div className='d-flex' style={{gap: '8px'}}>
                      <Button
                        style={{
                          width: '34px',
                          height: '34px',
                        }}
                        onClick={() => {
                          setToEditFAQ(faq)
                          setIsModalOpen(true)
                        }}
                      >
                        <img
                          alt='edit-icon'
                          src={toAbsoluteUrl('/media/icon/edit.svg')}
                          className=''
                          style={{height: '18px', width: '18px'}}
                        />
                      </Button>

                      <Button
                        style={{
                          width: '34px',
                          height: '34px',
                        }}
                        onClick={() => {
                          setToDeleteFAQ(faq)
                          setIsDeleteModalOpen(true)
                        }}
                      >
                        <img
                          alt='trash-icon'
                          src={toAbsoluteUrl('/media/icon/trash.svg')}
                          className=''
                          style={{height: '18px', width: '18px'}}
                        />
                      </Button>
                    </div>
                  </div>
                ))}
              </Accordion>
            ) : (
              <div
                className='w-100 d-flex justify-content-center align-items-center'
                style={{height: '245px'}}
              >
                <div className='d-flex flex-column align-items-center gap-5'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      borderRadius: '12px',
                      height: '48px',
                      width: '48px',
                    }}
                  >
                    <img
                      alt='message-icon'
                      src={toAbsoluteUrl('/media/icon/message.svg')}
                      className=''
                      style={{height: '32px', width: '32px'}}
                    />
                  </div>

                  <span className='text-dark fs-6 fw-medium'>No FAQ added</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomDrawer>

      {isModalOpen && (
        <AddEditFAQModal
          isModalOpen={isModalOpen}
          hideModal={() => setIsModalOpen(false)}
          isEditMode={Boolean(toEditFAQ.id)}
          faq={toEditFAQ}
        />
      )}

      <Card
        title='Frequently Asked Questions'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        {addedFAQs.length > 0 ? (
          <Accordion className='neutral-accordion' activeKey={activeKey}>
            {addedFAQs.map((faq, index) => (
              <div
                key={faq.id}
                style={{
                  padding: '0.5px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  marginBottom: '16px',
                }}
              >
                <Accordion.Item
                  eventKey={`${index}`}
                  style={{
                    border: 'none',
                  }}
                >
                  <Accordion.Header
                    onClick={() => setActiveKey(activeKey === `${index}` ? '' : `${index}`)}
                  >
                    <span className='fw-medium fs-4 text-dark'>{faq.question}</span>
                  </Accordion.Header>

                  <Accordion.Body className='text-secondary-gray fs-6'>{faq.answer}</Accordion.Body>
                </Accordion.Item>
              </div>
            ))}
          </Accordion>
        ) : (
          <div
            className='w-100 d-flex justify-content-center align-items-center'
            style={{height: '350px'}}
          >
            <div className='d-flex flex-column align-items-center gap-5'>
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '12px',
                  height: '48px',
                  width: '48px',
                }}
              >
                <img
                  alt='message-icon'
                  src={toAbsoluteUrl('/media/icon/message.svg')}
                  className=''
                  style={{height: '32px', width: '32px'}}
                />
              </div>

              <span className='text-dark fs-6 fw-medium'>No FAQ added</span>
            </div>
          </div>
        )}
      </Card>

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => {
            setIsDeleteModalOpen(false)
          }}
          confirmBtnClickHandler={handleRemoveFAQ}
          toDeleteItemTitle='FAQ'
          isLoading={loading}
        />
      )}
    </div>
  )
}

export default FAQ
